import classNames from 'classnames'
import { useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useGetGiftTypes } from '../../../../../hooks/queries/gift-types/use-get-gift-types.ts'
import { useGetUserLevelStatus } from '../../../../../hooks/queries/level/use-get-user-level-status.ts'
import { useGlobalStore } from '../../../../../store/global-store.ts'
import type { GiftType } from '../../../../../types/lives/messages.ts'
import { useGetGameEnabledFirst } from '../../../../../hooks/queries/games/games-enableds/use-get-game-enabled-first.ts'

interface Props {
  isPackageSelected: boolean
  handleTogglePackageSelected: () => void
  token?: string | null
}

export function GiftsTypes({
  isPackageSelected,
  handleTogglePackageSelected,
  token,
}: Props) {
  const { selectedType } = useGlobalStore(
    useShallow((state) => ({
      selectedType: state.selectedType,
    })),
  )

  const { gameEnabled } = useGetGameEnabledFirst({ token })
  const { giftTypes } = useGetGiftTypes({ token })
  const { levelStatus } = useGetUserLevelStatus({ token })

  const visibleGiftTypes = (giftTypes ?? []).filter((giftType) => {
    if (giftType?.name?.includes('Package')) {
      return false
    }

    if (giftType?.isLocked && !gameEnabled?.enableGameLuckGiftBox) {
      return false
    }

    if (giftType?.isLuck && (levelStatus?.levelSpectatorId ?? 0) <= 10) {
      return false
    }

    return true
  })

  useEffect(() => {
    const hasSelectedTypeVisible = visibleGiftTypes.some(
      (giftType) => giftType?.id === selectedType?.id,
    )

    if (!selectedType?.id || !hasSelectedTypeVisible) {
      useGlobalStore.setState({
        selectedType: visibleGiftTypes[0],
      })
    }
  }, [selectedType, visibleGiftTypes])

  function handleClick(giftType: GiftType) {
    if (isPackageSelected) {
      handleTogglePackageSelected()
    }

    useGlobalStore.setState({
      selectedType: giftType,
    })
  }

  return (
    <div className="flex flex-row items-center justify-between border-b border-white/5">
      <div className="flex flex-row gap-5 overflow-y-scroll [&::-webkit-scrollbar]:hidden">
        {visibleGiftTypes.map((giftType) => {
          const isActive = selectedType?.id === giftType?.id && !isPackageSelected

          return (
            <span
              key={giftType?.id}
              onClick={() => handleClick(giftType)}
              className={classNames(
                'relative pb-1.5 text-[13px] text-center whitespace-nowrap transition-colors',
                {
                  "text-white font-semibold after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-[3px] after:w-5 after:rounded-full after:bg-hug-purple":
                    isActive,
                  'text-white/50': !isActive,
                },
              )}
            >
              {giftType?.name}
            </span>
          )
        })}
      </div>
    </div>
  )
}
